import formatDateWithTimezone from "@insite/client-framework/Common/Utilities/formatDateWithTimezone";
import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import {
    CartResult,
    UpdateCartApiParameter,
    updateCartWithResult,
} from "@insite/client-framework/Services/CartService";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import updateCurrentAccount from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/UpdateCurrentAccount";

const convertDateToApiFormat = (date: Date | null) => (date ? formatDateWithTimezone(date) : "");

type HandlerType = ApiHandlerDiscreteParameter<HasOnSuccess<string>, UpdateCartApiParameter, CartResult>;

export const DispatchBeginPlaceOrderForApproval: HandlerType = props => {
    props.dispatch({
        type: "Pages/CheckoutReviewAndSubmit/BeginPlaceOrderForApproval",
    });
};

export const SignUpForNewsletter: HandlerType = ({ getState, dispatch }) => {
    const state = getState();
    const { cartId, signUpForNewsletter } = state.pages.checkoutReviewAndSubmit;
    if (!signUpForNewsletter) {
        return;
    }

    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart } = cartState;
    if (!cart) {
        return;
    }

    const account = getCurrentAccountState(state).value;
    if (!account || account.isSubscribed) {
        return;
    }

    const billTo = getBillToState(state, cart.billToId).value;
    const emailForSubscribe = account.isGuest ? billTo?.email : account.email;
    if (!emailForSubscribe) {
        return;
    }

    dispatch(
        updateCurrentAccount({
            account: { ...account, isSubscribed: true, email: emailForSubscribe },
        }),
    );
};

export const PopulateApiParameter: HandlerType = props => {
    const state = props.getState();
    const {
        pages: {
            checkoutReviewAndSubmit: { cartId, requestedDeliveryDate, requestedPickupDate },
        },
    } = state;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    if (!cart) {
        throw new Error("There was no current cart and we are trying to place the current cart as an order.");
    }

    const tempDeliveryDate =
        typeof requestedDeliveryDate !== "undefined" ? requestedDeliveryDate : cart.requestedDeliveryDateDisplay;
    const tempPickupDate =
        typeof requestedPickupDate !== "undefined" ? requestedPickupDate : cart.requestedPickupDateDisplay;

    props.apiParameter = {
        cart: {
            ...cart,
            status: "AwaitingApproval",
            requestedDeliveryDate: convertDateToApiFormat(tempDeliveryDate),
            requestedPickupDate: convertDateToApiFormat(tempPickupDate),
        },
    };
};

export const UpdateCart: HandlerType = async props => {
    const result = await updateCartWithResult(props.apiParameter);
    if (result.successful) {
        props.apiResult = result.result;
    } else {
        props.dispatch({
            type: "Pages/CheckoutReviewAndSubmit/SetPlaceOrderErrorMessage",
            errorMessage: result.errorMessage,
        });
        props.dispatch({
            type: "Pages/CheckoutReviewAndSubmit/CompletePlaceOrderForApproval",
        });
        return false;
    }
};

export const ReloadCurrentCart: HandlerType = props => {
    props.dispatch(loadCurrentCart());
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.parameter.onSuccess) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess(props.apiResult.cart.id);
    }
};

export const DispatchCompletePlaceOrderForApproval: HandlerType = props => {
    props.dispatch({
        type: "Pages/CheckoutReviewAndSubmit/CompletePlaceOrderForApproval",
    });
};

export const DispatchResetOrders: HandlerType = props => {
    props.dispatch({
        type: "Data/Orders/Reset",
    });
};

export const DispatchResetQuotes: HandlerType = props => {
    const state = props.getState();
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    if (cartId) {
        props.dispatch({
            type: "Data/Quotes/Reset",
        });
        props.dispatch({
            type: "Data/JobQuotes/Reset",
        });
    }
};

export const chain = [
    DispatchBeginPlaceOrderForApproval,
    SignUpForNewsletter,
    PopulateApiParameter,
    UpdateCart,
    ReloadCurrentCart,
    ExecuteOnSuccessCallback,
    DispatchCompletePlaceOrderForApproval,
    DispatchResetOrders,
    DispatchResetQuotes,
];

const placeOrderForApproval = createHandlerChainRunner(chain, "PlaceOrderForApproval");
export default placeOrderForApproval;
